import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { TagsIcon, Trash2Icon } from "lucide-react";
import { LoadingState } from "@/ds/custom/LoadingState";
import { NumericInput } from "@/ds/custom/NumericInput";
import { Selector } from "@/ds/custom/Selector";
import { StatePanel } from "@/ds/custom/StatePanel";
import { Button } from "@/ds/shadcn/components/ui/button";
import { TableCell } from "@/ds/shadcn/components/ui/table";
import { useTitle } from "@/lib/common/useTitle";
import { CurrencyOptions } from "@/lib/domain/Currency";
import { DataView } from "@/features/common/components/dataview/DataView";
import { HeaderBar } from "@/features/common/components/HeaderBar";
import { HeaderBarTools } from "@/features/common/components/HeaderBarTools";
import { SmallProductDisplay } from "@/features/common/components/SmallProductDisplay";
import { ListItem, ListItemMenu } from "@/features/common/components/dataview/ListItem";
import { usePriceOverride } from "@/features/priceOverrides/hooks/usePriceOverride";
import { usePriceOverrideDelete } from "@/features/priceOverrides/api/usePriceOverrideDelete";
import { usePriceOverrideDetails, type PriceOverrideDetailsResponseModel } from "@/features/priceOverrides/api/usePriceOverrideDetails";
import { SplitScreen, SplitScreenLeftPanel, SplitScreenRightPanel } from "@/features/common/components/SplitScreen";
import { VariantSelector } from "@/features/common/components/VariantSelector";
import { usePriceOverrideCreate } from "@/features/priceOverrides/api/usePriceOverrideCreate";
import type { GetProductAndVariantsMarketPriceResponseModel, VariantMarketPriceResponseModel } from "@/features/products/api/useProduct";
import { useNotifyMutationResult } from "@/lib/common/useNotifyMutationResult";
import { useNotifyMutationSuccess } from "@/lib/common/useNotifyMutationSuccess";
import { GalleryItem, GalleryItemMenu, GalleryItemProperty } from "@/features/common/components/dataview/GalleryItem";
import { DropdownMenuGroup, DropdownMenuItem } from "@/ds/shadcn/components/ui/dropdown-menu";
import { ModalConfirm } from "@/features/common/components/ModalConfirm";
import { ProductView } from "@/features/products/components/ProductView";

export const PagePriceOverrides: FC = () => {
	useTitle("Price Overrides");

	const breadcrumb = useMemo(() => [
		{ label: "Dashboard", href: "/dashboard" },
		{ label: "Price Overrides" }
	], []);

	const [variantSelectorVisible, setVariantSelectorVisible] = useState(false);

	return (
		<>
			<HeaderBar title="Price Overrides" description="Manage your custom price overrides" breadcrumb={breadcrumb} />
			<HeaderBarTools>
				<Button variant="outline" onClick={() => setVariantSelectorVisible(true)} disabled={variantSelectorVisible}>
					<TagsIcon /> Create
				</Button>
			</HeaderBarTools>

			<PagePriceOverridesInternal variantSelectorVisible={variantSelectorVisible} setVariantSelectorVisible={setVariantSelectorVisible} />
		</>
	);
};

interface PagePriceOverridesInternalProps {
	variantSelectorVisible: boolean;
	setVariantSelectorVisible: (visible: boolean) => void;
}

const PagePriceOverridesInternal: FC<PagePriceOverridesInternalProps> = props => {
	const {
		variantSelectorVisible,
		setVariantSelectorVisible
	} = props;

	const priceOverrideDetails = usePriceOverrideDetails();
	const ref = useRef<HTMLDivElement>(null);
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

	const createPriceOverride = usePriceOverrideCreate();
	useNotifyMutationResult(createPriceOverride, "Price Override Created", "Your price override was created successfully.");

	const deletePriceOverride = usePriceOverrideDelete();
	useNotifyMutationSuccess(deletePriceOverride, "Price Override Deleted", "Your price override was deleted successfully.");

	const [priceOverrideIdToDelete, setPriceOverrideIdToDelete] = useState<string | null>(null);

	useEffect(() => {
		if (!deletePriceOverride.isSuccess) {
			return;
		}

		setPriceOverrideIdToDelete(null);
	}, [deletePriceOverride.isSuccess]);

	const onVariantSelected = useCallback((_product: GetProductAndVariantsMarketPriceResponseModel, variant: VariantMarketPriceResponseModel) => {
		if (!variant.marketPrice) {
			return;
		}

		createPriceOverride.mutate({
			variantId: variant.variantId,
			price: variant.marketPrice
		});
	}, [createPriceOverride]);

	const onSelectProduct = useCallback((productId: string) => {
		setSelectedProductId(productId);
		setVariantSelectorVisible(false);
	}, [setVariantSelectorVisible]);

	const onClosePanel = useCallback(() => {
		setSelectedProductId(null);
		setVariantSelectorVisible(false);
	}, [setVariantSelectorVisible]);

	const additionalProps = useMemo(() => ({
		onDelete: setPriceOverrideIdToDelete,
		onSelectProduct
	}), [onSelectProduct]);

	return (
		<>
			<SplitScreen fixed visible={variantSelectorVisible || createPriceOverride.isPending || !!selectedProductId} onVisibleChange={visible => !visible && onClosePanel()}>
				<SplitScreenLeftPanel ref={scrollRef}>
					<DataView
						forQuery={priceOverrideDetails}
						scrollRef={scrollRef}
						getKey={x => x.variantId}
						listViewHeaderLabels={["Product", "Override", "Currency", ""]}
						listViewHeaderClassNames={["min-w-0", "w-50 @2xl:w-64", "w-48 @2xl:w-64", "w-16"]}
						listComponent={PagePriceOverridesDataViewListItem}
						galleryComponent={PagePriceOverridesDataViewGalleryItem}
						galleryMinWidth={192}
						additionalProps={additionalProps} >

						<StatePanel
							icon={TagsIcon}
							title="Price Overrides"
							description="Price overrides allow you to set a custom price for a specific product.">
							<Button variant="outline" disabled={variantSelectorVisible} onClick={() => setVariantSelectorVisible(true)}>Create Price Override</Button>
						</StatePanel>
					</DataView>
				</SplitScreenLeftPanel>
				<SplitScreenRightPanel ref={ref}>
					{
						createPriceOverride.isPending ? (
							<LoadingState />
						) : variantSelectorVisible ? (
							<VariantSelector onSelect={onVariantSelected} onCancel={onClosePanel} scrollRef={ref} />
						) : selectedProductId && (
							<ProductView className="min-h-full" productId={selectedProductId} />
						)
					}
				</SplitScreenRightPanel>
			</SplitScreen>

			<ModalConfirm
				title="Delete Price Override"
				description="Are you sure you want to delete this price override?"
				confirmText="Delete"
				confirmDestructive
				isLoading={deletePriceOverride.isPending}
				error={deletePriceOverride.error}
				open={!!priceOverrideIdToDelete}
				onOpenChange={open => {
					if (!open) {
						setPriceOverrideIdToDelete(null);
						deletePriceOverride.reset();
					}
				}}
				onConfirm={() => priceOverrideIdToDelete && deletePriceOverride.mutate(priceOverrideIdToDelete)} />
		</>
	);
};

interface PagePriceOverridesDataViewProps extends PriceOverrideDetailsResponseModel {
	onDelete: (priceOverrideId: string) => void;
	onSelectProduct: (productId: string) => void;
}

const PagePriceOverridesDataViewListItem: FC<PagePriceOverridesDataViewProps> = priceOverride => {
	const {
		priceOverrideId,
		productId,
		price,
		onDelete,
		onSelectProduct
	} = priceOverride;

	const [value, setValue] = useState(price.value);
	const [currency, setCurrency] = useState(price.currency);

	const priceOverrideUpdate = usePriceOverride(value, currency, priceOverride);

	const onClick = useCallback(() => onSelectProduct(productId), [onSelectProduct, productId]);

	return (
		<ListItem onClick={onClick} isLoading={priceOverrideUpdate.isPending} error={priceOverrideUpdate.error}>
			<TableCell className="min-w-0">
				<SmallProductDisplay {...priceOverride} />
			</TableCell>
			<TableCell className="w-4" onClick={e => e.stopPropagation()}>
				<NumericInput
					value={value}
					onChange={setValue}
					minValue={0}
					maxValue={500000} />
			</TableCell>
			<TableCell className="w-4" onClick={e => e.stopPropagation()}>
				<Selector
					className="w-full bg-background"
					value={currency}
					onChange={setCurrency}
					placeholder="Price Override currency"
					options={CurrencyOptions}
					isLoading={false} />
			</TableCell>
			<TableCell className="w-4" onClick={e => e.stopPropagation()}>
				<ListItemMenu>
					<DropdownMenuGroup>
						<DropdownMenuItem variant="destructive" onClick={() => onDelete(priceOverrideId)}>
							<Trash2Icon />
							Delete
						</DropdownMenuItem>
					</DropdownMenuGroup>
				</ListItemMenu>
			</TableCell>
		</ListItem>
	);
};

const PagePriceOverridesDataViewGalleryItem: FC<PagePriceOverridesDataViewProps> = priceOverride => {
	const {
		productName,
		fcId,
		imageUrl,
		priceOverrideId,
		productId,
		price,
		onDelete,
		onSelectProduct
	} = priceOverride;

	const [value, setValue] = useState(price.value);
	const [currency, setCurrency] = useState(price.currency);

	const priceOverrideUpdate = usePriceOverride(value, currency, priceOverride);

	const onClick = useCallback(() => onSelectProduct(productId), [onSelectProduct, productId]);

	return (
		<GalleryItem title={productName} description={fcId} imageUrl={imageUrl} onClick={onClick} isLoading={priceOverrideUpdate.isPending} error={priceOverrideUpdate.error}>
			<GalleryItemMenu>
				<DropdownMenuGroup>
					<DropdownMenuItem variant="destructive" onClick={() => onDelete(priceOverrideId)}>
						<Trash2Icon />
						Delete
					</DropdownMenuItem>
				</DropdownMenuGroup>
			</GalleryItemMenu>
			<GalleryItemProperty>
				<div className="w-full" onClick={e => e.stopPropagation()}>
					<NumericInput
						value={value}
						onChange={setValue}
						minValue={0}
						maxValue={500000} />
				</div>
			</GalleryItemProperty>
			<GalleryItemProperty>
				<div className="w-full" onClick={e => e.stopPropagation()}>
					<Selector
						className="w-full bg-background"
						value={currency}
						onChange={setCurrency}
						placeholder="Price Override currency"
						options={CurrencyOptions}
						isLoading={false} />
				</div>
			</GalleryItemProperty>
		</GalleryItem>
	);
};
